import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BookOpen, Download } from "lucide-react";

export default function EBooks() {
  const { user } = useAuth();
  const [ebooks, setEbooks] = useState([]);
  const [selected, setSelected] = useState(null);
  const [file, setFile] = useState(null);
  const [settings, setSettings] = useState({ upi_id: "" });
  const [purchases, setPurchases] = useState([]);

  useEffect(() => {
    api.get("/ebooks").then((r) => setEbooks(r.data));
    api.get("/settings/public").then((r) => setSettings(r.data));
    if (user) api.get("/my/purchases").then((r) => setPurchases(r.data));
  }, [user]);

  const purchased = (id) => purchases.find((p) => p.ebook_id === id && ["approved","free"].includes(p.status));
  const pending = (id) => purchases.find((p) => p.ebook_id === id && p.status === "payment_review");

  const submitPurchase = async () => {
    if (!user) return alert("Please login first");
    if (!selected.is_free && !file) return alert("Upload payment screenshot");
    let dataUrl = null;
    if (file) dataUrl = await new Promise((res)=>{ const r=new FileReader(); r.onload=()=>res(r.result); r.readAsDataURL(file); });
    await api.post(`/ebooks/${selected.id}/purchase`, { payment_screenshot: dataUrl });
    const { data } = await api.get("/my/purchases");
    setPurchases(data);
    setSelected(null); setFile(null);
    alert(selected.is_free ? "Free download unlocked!" : "Payment submitted for review.");
  };

  const download = async (id) => {
    const { data } = await api.get(`/ebooks/${id}/download`);
    window.open(data.pdf_url, "_blank");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" data-testid="ebooks-page">
      <div className="overline mb-1">Digital Library</div>
      <h1 className="font-display font-black text-3xl sm:text-4xl mb-2" style={{color:"#232F2F"}}>E-Books & Checklists</h1>
      <p className="text-[#5A6D6D] mb-6 max-w-2xl">Practical, pediatrician-written handbooks — download instantly after payment approval.</p>

      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
        {ebooks.map((eb) => {
          const own = purchased(eb.id);
          const pend = pending(eb.id);
          return (
            <div key={eb.id} className="card-soft flex flex-col" data-testid={`ebook-${eb.id}`}>
              <div className="aspect-[4/3] rounded-xl overflow-hidden bg-[#F0F3F3] mb-3">
                {eb.cover_image ? <img src={eb.cover_image} alt={eb.title} className="w-full h-full object-cover"/> : <div className="w-full h-full flex items-center justify-center"><BookOpen color="#2D7A78" size={40}/></div>}
              </div>
              <div className="flex items-center gap-2 mb-1">
                <span className="chip">{eb.category}</span>
                {eb.is_free ? <span className="chip chip-peach">FREE</span> : <span className="chip">{eb.language}</span>}
              </div>
              <div className="font-display font-bold">{eb.title}</div>
              <div className="text-xs text-[#5A6D6D] mt-1 flex-1">{eb.short_desc}</div>
              <div className="mt-3 flex items-center justify-between">
                <div className="font-bold text-[#2D7A78]">{eb.is_free ? "Free" : `₹${eb.price}`}</div>
                {own ? (
                  <button onClick={() => download(eb.id)} className="btn-primary text-sm" data-testid={`download-${eb.id}`}><Download size={14}/> Download</button>
                ) : pend ? (
                  <span className="chip chip-peach">Under review</span>
                ) : (
                  <button onClick={() => setSelected(eb)} className="btn-outline text-sm" data-testid={`buy-${eb.id}`}>{eb.is_free ? "Get Free" : "Buy"}</button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)} data-testid="ebook-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e)=>e.stopPropagation()}>
            <h3 className="font-display font-bold text-lg mb-2">{selected.title}</h3>
            {selected.is_free ? (
              <p className="text-sm text-[#5A6D6D]">Free download. Click confirm to unlock.</p>
            ) : (
              <>
                <p className="text-sm text-[#5A6D6D] mb-3">Pay ₹{selected.price} to UPI: <span className="font-mono font-bold text-[#232F2F]">{settings.upi_id}</span></p>
                <img alt="qr" src={`https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=upi://pay?pa=${encodeURIComponent(settings.upi_id)}&am=${selected.price}&cu=INR`} className="mx-auto mb-3"/>
                <input type="file" accept="image/*" onChange={(e)=>setFile(e.target.files?.[0])} className="block w-full text-sm mb-3" data-testid="ebook-payment-screenshot"/>
              </>
            )}
            <div className="flex gap-2">
              <button onClick={submitPurchase} className="btn-primary flex-1 justify-center" data-testid="confirm-purchase">Confirm</button>
              <button onClick={()=>setSelected(null)} className="btn-outline flex-1 justify-center">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
